import React from 'react';

import type { WebsiteHealthResponse, WebsiteSurfaceKey } from '@utazzo/shared';

import { SectionHeading } from '@/features/website-dashboard/components/ui';
import { fmt, pct, SURFACE_META, TEAL } from '@/features/website-dashboard/lib';

/**
 * One tile per website surface: the headline total, a live / draft / other
 * split bar, and a "gaps" badge when live rows of that surface are missing
 * fields. Counts come from `/dashboard/website-health` - the same aggregate
 * the breakdown tables read, so the tile total matches the table total.
 */

const Legend = (): React.JSX.Element => (
  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#6b7075', whiteSpace: 'nowrap' }}>
    <LegendItem color={TEAL} opacity={1} label="Live" />
    <LegendItem color={TEAL} opacity={0.25} label="Draft" />
    <LegendItem color="#f59e0b" opacity={1} label="Other" />
  </div>
);

const LegendItem = ({
  color,
  opacity,
  label,
}: {
  color: string;
  opacity: number;
  label: string;
}): React.JSX.Element => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 16, height: 5, borderRadius: 3, background: color, opacity }} />
    {label}
  </span>
);

interface Props {
  health: WebsiteHealthResponse;
}

export const SurfaceTiles = ({ health }: Props): React.JSX.Element => {
  const counts = new Map(health.surfaces.map((s) => [s.key, s]));
  const gapBySurface = new Map<WebsiteSurfaceKey, number>(
    health.incompleteCounts.map((c) => [c.surface, c.live]),
  );

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionHeading title="Surfaces" right={<Legend />} />
      <div className="wd-tiles" style={{ display: 'grid', gap: 10 }}>
        {SURFACE_META.filter((m) => m.key !== 'blog_category').map((meta) => {
          const c = counts.get(meta.key);
          const total = c?.total ?? 0;
          const live = c?.live ?? 0;
          const draft = c?.draft ?? 0;
          const gap = gapBySurface.get(meta.key) ?? 0;
          return (
            <div
              key={meta.key}
              onClick={() => window.open(meta.to, '_blank', 'noopener,noreferrer')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '14px 16px',
                background: '#fff',
                border: '1px solid #e3e5e5',
                borderRadius: 16,
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: meta.bg,
                      color: meta.fg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {meta.glyph}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#55595e',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {meta.label}
                  </span>
                </div>
                {gap > 0 && (
                  <span
                    title="live rows missing fields"
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: '#b45309',
                      background: '#fef3c7',
                      borderRadius: 999,
                      padding: '2px 7px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmt(gap)} gaps
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  letterSpacing: '-.03em',
                  color: total > 0 ? '#17171a' : '#a0a5a9',
                  lineHeight: 1,
                }}
              >
                {fmt(total)}
              </div>
              <div style={{ display: 'flex', height: 5, gap: 2 }}>
                <span
                  style={{ width: `${pct(live, total)}%`, background: TEAL, borderRadius: 3 }}
                />
                <span
                  style={{
                    width: `${pct(draft, total)}%`,
                    background: TEAL,
                    opacity: 0.25,
                    borderRadius: 3,
                  }}
                />
                <span style={{ flex: 1, background: '#f59e0b', borderRadius: 3 }} />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontSize: 11,
                  color: '#8a9095',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>
                  {meta.liveWord} <b style={{ color: '#17171a', fontWeight: 700 }}>{fmt(live)}</b>
                </span>
                <span>
                  Draft <b style={{ color: '#17171a', fontWeight: 700 }}>{fmt(draft)}</b>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
